import { useMemo, useRef, useState, useEffect } from "react";
import {
  Search,
  Upload,
  ChevronDown,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileText,
  Activity,
  X,
  Plus,
  MoreHorizontal,
  Edit3,
  Trash2,
  FileWarning,
} from "lucide-react";
import { ImportCsvDialog } from "../components/ImportCsvDialog";
import { ReceiptPreview } from "../components/ReceiptPreview";
import { format, parseISO } from "date-fns";
import { ReceiptUploadButton } from "../components/ReceiptUploadButton";
import { NewInvoiceDialog } from "../components/NewInvoiceDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useReceipts } from "../../lib/receipts/useReceipts";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useVendors } from "../../lib/vendors/useVendors";
import { useAuth } from "../../lib/auth/useAuth";
import type {
  Invoice,
  InvoiceDirection,
  InvoiceStatus,
  NewInvoiceInput,
} from "../../lib/invoices/types";
import { VendorLogo } from "../components/VendorLogo";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";

type DirectionFilter = "all" | InvoiceDirection;

function formatDate(iso: string, pattern = "MMM d, yyyy"): string {
  try {
    return format(parseISO(iso), pattern);
  } catch {
    return iso;
  }
}

function formatAmount(cents: number, currency = "USD"): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  return formatter.format(cents / 100);
}

interface StatusBadgeProps {
  status: InvoiceStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  switch (status) {
    case "paid":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Paid
        </span>
      );
    case "pending":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-800 rounded-lg border border-amber-200">
          <Clock className="w-3.5 h-3.5" /> Pending
        </span>
      );
    case "approved":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Approved
        </span>
      );
    case "rejected":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-lg border border-red-100">
          <X className="w-3.5 h-3.5" /> Rejected
        </span>
      );
    case "overdue":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-lg border border-red-100">
          <AlertCircle className="w-3.5 h-3.5" /> Overdue
        </span>
      );
    case "draft":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-700 rounded-lg border border-slate-200">
          <FileText className="w-3.5 h-3.5" /> Draft
        </span>
      );
    case "cancelled":
      return (
        <span data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg border border-slate-200">
          <X className="w-3.5 h-3.5" /> Cancelled
        </span>
      );
  }
}

export function InvoicesHub() {
  const { invoices: allInvoices, remove, submit, approve, reject } = useInvoices();
  const { getByInvoice } = useReceipts();
  const { getById: getVendor } = useVendors();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { scope, pinned } = useEffectiveScopeForTab("invoices");

  const currentUserId = user?.id ?? null;
  const canApproveInvoices = can("approve:invoices");
  const canWrite = can("write:invoices_own") || can("write:invoices_any");

  // Wave 22 — apply the effective view scope. "mine" restricts to rows the
  // current user either created OR submitted; "company" leaves the list
  // untouched. Checking `createdBy` matters because drafts have
  // submittedBy=null until submitted, so a submittedBy-only filter hid
  // the creator's own drafts. Null user id (auth still hydrating) collapses
  // to an empty list to avoid leaking other users' rows on first paint.
  const invoices = useMemo(() => {
    if (scope === "company") return allInvoices;
    if (!currentUserId) return [];
    return allInvoices.filter(
      (i) => i.createdBy === currentUserId || i.submittedBy === currentUserId,
    );
  }, [allInvoices, scope, currentUserId]);

  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [moreOpenFor, setMoreOpenFor] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState<number | null>(null);

  const filteredInvoices = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (directionFilter !== "all" && inv.direction !== directionFilter) {
        return false;
      }
      if (needle && !inv.counterparty.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
  }, [invoices, directionFilter, searchTerm]);

  const selectedInvoice: Invoice | null = useMemo(() => {
    if (!selectedInvoiceId) return null;
    return invoices.find((inv) => inv.id === selectedInvoiceId) ?? null;
  }, [invoices, selectedInvoiceId]);

  // Clear selection if the selected invoice was removed from the list.
  useEffect(() => {
    if (selectedInvoiceId && !selectedInvoice) {
      setSelectedInvoiceId(null);
      setDetailModalOpen(false);
    }
  }, [selectedInvoiceId, selectedInvoice]);

  // Close the More menu on outside click.
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!moreOpenFor) return;
    function onDown(e: MouseEvent) {
      if (!moreMenuRef.current) return;
      if (!moreMenuRef.current.contains(e.target as Node)) {
        setMoreOpenFor(null);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [moreOpenFor]);

  const linkedReceipt = selectedInvoice
    ? getByInvoice(selectedInvoice.id)
    : undefined;

  const editInitialValues: Partial<NewInvoiceInput> | undefined =
    editInvoiceId
      ? (() => {
          const inv = invoices.find((i) => i.id === editInvoiceId);
          if (!inv) return undefined;
          return {
            counterparty: inv.counterparty,
            direction: inv.direction,
            amountCents: inv.amountCents,
            currency: inv.currency,
            issuedOn: inv.issuedOn,
            dueOn: inv.dueOn,
            status: inv.status,
            notes: inv.notes ?? undefined,
          };
        })()
      : undefined;

  async function handleDelete(id: string): Promise<void> {
    await remove(id);
    if (selectedInvoiceId === id) {
      setSelectedInvoiceId(null);
      setDetailModalOpen(false);
    }
    setDeleteConfirmId(null);
  }

  function openEdit(id: string): void {
    setEditInvoiceId(id);
    setEditOpen(true);
    setMoreOpenFor(null);
  }

  return (
    <div
      className="h-full flex flex-col gap-6 max-w-[1440px] mx-auto"
      data-scope={scope}
      data-testid="invoices-hub-root"
    >
      {/* Top Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-slate-500 mt-1">
            Manage all incoming and outgoing invoices
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              data-testid="invoices-hub-import-csv"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              data-testid="invoices-hub-new"
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 bg-[#0070F2] hover:bg-[#005BCA] text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" /> New Invoice
            </button>
          )}
          <ReceiptUploadButton
            linkedInvoiceId={selectedInvoice?.id}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <Upload className="w-4 h-4" />
            Upload proof
          </ReceiptUploadButton>
        </div>
      </div>

      {/* Import success banner */}
      {importSuccessCount !== null && (
        <div
          data-testid="invoices-hub-import-success"
          className="flex items-center justify-between gap-3 px-4 py-2 bg-green-50 border border-green-200 text-green-800 text-sm rounded-xl"
        >
          <span>
            Successfully imported {importSuccessCount} invoice
            {importSuccessCount !== 1 ? "s" : ""}.
          </span>
          <button
            type="button"
            onClick={() => setImportSuccessCount(null)}
            className="p-1 text-green-700 hover:text-green-900 hover:bg-green-100 rounded"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
          {(
            [
              { key: "all", label: "All" },
              { key: "incoming", label: "Incoming" },
              { key: "outgoing", label: "Outgoing" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setDirectionFilter(key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                directionFilter === key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="invoices-hub-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search counterparty..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] transition-all"
            />
          </div>
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Date filters coming soon"
            className="flex items-center gap-2 px-4 py-2 text-slate-400 bg-white border border-slate-200 font-medium rounded-xl shadow-sm text-sm whitespace-nowrap cursor-not-allowed"
          >
            <Clock className="w-4 h-4 text-slate-300" /> Last 30 days{" "}
            <ChevronDown className="w-3.5 h-3.5 ml-1" />
          </button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="flex flex-1 gap-6 min-h-0 relative">
        {/* Invoices List */}
        <div
          className={`flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col transition-all duration-300 ${
            selectedInvoice ? "w-2/3 max-w-[calc(100%-400px)]" : "w-full"
          }`}
        >
          {filteredInvoices.length === 0 ? (
            <div
              data-testid="invoices-hub-empty-state"
              className="flex flex-col items-center justify-center gap-3 text-center px-6 py-16 text-slate-500 h-full"
            >
              <FileWarning className="w-8 h-8 text-slate-300" />
              <div className="text-sm font-medium text-slate-700">
                {invoices.length === 0
                  ? "No invoices yet"
                  : "No invoices match your filters"}
              </div>
              <div className="text-xs text-slate-500">
                {invoices.length === 0 ? (
                  <>
                    Click <span className="font-semibold">New Invoice</span> to
                    add one.
                  </>
                ) : (
                  <>Try clearing the search or changing the direction filter.</>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto h-full">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-slate-50/90 backdrop-blur z-10">
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Counterparty
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Issued
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Proof
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInvoices.map((inv) => {
                    const receipt = getByInvoice(inv.id);
                    return (
                      <tr
                        key={inv.id}
                        data-testid={`invoice-row-${inv.id}`}
                        onClick={() => setSelectedInvoiceId(inv.id)}
                        className={`hover:bg-slate-50 transition-colors cursor-pointer group ${
                          selectedInvoiceId === inv.id
                            ? "bg-blue-50/50 hover:bg-blue-50/50"
                            : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-900 flex items-center gap-2">
                            {inv.vendorId &&
                              (() => {
                                const v = getVendor(inv.vendorId);
                                return v ? <VendorLogo vendor={v} size={20} /> : null;
                              })()}
                            <span className="truncate">{inv.counterparty}</span>
                          </div>
                          <div className="text-sm text-slate-500">
                            {formatAmount(inv.amountCents, inv.currency)} •{" "}
                            {inv.dueOn ? `due ${formatDate(inv.dueOn)}` : "no due date"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {inv.direction === "incoming" ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100">
                              <ArrowDownLeft className="w-3.5 h-3.5" /> Incoming
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100">
                              <ArrowUpRight className="w-3.5 h-3.5" /> Outgoing
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-900">
                            {formatAmount(inv.amountCents, inv.currency)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-700 font-medium">
                            {formatDate(inv.issuedOn)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={inv.status} />
                        </td>
                        <td className="px-6 py-4">
                          {receipt ? (
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
                              <FileText className="w-4 h-4 text-[#3B82F6]" />{" "}
                              Ready
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600">
                              <AlertCircle className="w-4 h-4" /> Missing
                            </span>
                          )}
                        </td>
                        <td
                          className="px-6 py-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="relative inline-block"
                            ref={
                              moreOpenFor === inv.id ? moreMenuRef : null
                            }
                          >
                            <button
                              type="button"
                              data-testid={`invoice-row-more-${inv.id}`}
                              onClick={() =>
                                setMoreOpenFor(
                                  moreOpenFor === inv.id ? null : inv.id,
                                )
                              }
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                              aria-label="More actions"
                            >
                              <MoreHorizontal className="w-5 h-5" />
                            </button>
                            {moreOpenFor === inv.id && canWrite && (
                              <div className="absolute right-0 mt-1 w-36 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                                <button
                                  type="button"
                                  data-testid={`invoice-row-edit-${inv.id}`}
                                  onClick={() => openEdit(inv.id)}
                                  className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Edit3 className="w-4 h-4" /> Edit
                                </button>
                                <button
                                  type="button"
                                  data-testid={`invoice-row-delete-${inv.id}`}
                                  onClick={() => {
                                    setDeleteConfirmId(inv.id);
                                    setMoreOpenFor(null);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Panel (Right Side) */}
        {selectedInvoice && (
          <div className="w-[400px] flex-shrink-0 bg-white rounded-2xl shadow-lg border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-right-8">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h2 className="font-semibold text-slate-900">Invoice Details</h2>
              <button
                type="button"
                onClick={() => setSelectedInvoiceId(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                aria-label="Close detail panel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Preview */}
              <div className="aspect-[3/4] bg-slate-100 rounded-xl overflow-hidden border border-slate-200 relative flex items-center justify-center">
                {linkedReceipt ? (
                  <ReceiptPreview
                    testId="invoice-receipt-preview"
                    receipt={linkedReceipt}
                    alt={`Invoice proof for ${selectedInvoice.counterparty}`}
                    className="object-contain w-full h-full opacity-90 mix-blend-multiply"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-400 px-4 py-8 text-center">
                    <FileText className="w-8 h-8" />
                    <div className="text-sm font-medium">No proof attached</div>
                    <div className="text-xs">
                      Upload an invoice document to attach
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {selectedInvoice.counterparty}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      Issued {formatDate(selectedInvoice.issuedOn)}
                      {selectedInvoice.dueOn
                        ? ` • Due ${formatDate(selectedInvoice.dueOn)}`
                        : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-slate-900">
                      {formatAmount(
                        selectedInvoice.amountCents,
                        selectedInvoice.currency,
                      )}
                    </span>
                    <p className="text-sm font-medium mt-1 text-slate-500 capitalize">
                      {selectedInvoice.direction}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 mb-6">
                  <StatusBadge status={selectedInvoice.status} />
                </div>
              </div>

              {/* Proof Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Proof & Compliance
                </h4>
                {linkedReceipt ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 text-blue-800 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FileText className="w-4 h-4" /> Invoice attached
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-amber-50 text-amber-800 rounded-xl border border-amber-100">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <AlertCircle className="w-4 h-4" /> Missing invoice document
                    </div>
                    <ReceiptUploadButton
                      linkedInvoiceId={selectedInvoice.id}
                      data-testid="invoice-upload"
                      className="text-xs font-semibold text-amber-900 hover:underline"
                    >
                      Upload
                    </ReceiptUploadButton>
                  </div>
                )}

                {selectedInvoice.status === "paid" && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 text-slate-700 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Activity className="w-4 h-4 text-slate-400" /> Payment
                      matched
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>

              {selectedInvoice.notes && (
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Notes
                  </h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {selectedInvoice.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 space-y-2">
              {(() => {
                const inv = selectedInvoice;
                if (!inv) return null;
                const canSubmit =
                  (inv.submittedBy == null ||
                    inv.submittedBy === currentUserId) &&
                  ["draft", "rejected"].includes(inv.status);
                const canApprove = canApproveInvoices && inv.status === "pending";
                const canReject = canApprove;

                async function doSubmit() {
                  await submit(inv!.id);
                }
                async function doApprove() {
                  await approve(inv!.id);
                }
                async function doReject() {
                  const reason = typeof window !== "undefined"
                    ? window.prompt("Reason for rejection?") ?? ""
                    : "";
                  if (!reason.trim()) return;
                  await reject(inv!.id, reason.trim());
                }

                return (
                  <>
                    {canSubmit && (
                      <button
                        type="button"
                        data-testid={`invoice-detail-submit-${inv.id}`}
                        onClick={() => { void doSubmit(); }}
                        className="w-full py-2.5 bg-[#0070F2] hover:bg-[#005BCA] text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                      >
                        Submit for review
                      </button>
                    )}
                    {canApprove && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          data-testid={`invoice-detail-approve-${inv.id}`}
                          onClick={() => { void doApprove(); }}
                          className="py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          data-testid={`invoice-detail-reject-${inv.id}`}
                          onClick={() => { void doReject(); }}
                          className={`py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors ${canReject ? "" : "hidden"}`}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {inv.status === "rejected" && inv.rejectionReason && (
                      <div
                        data-testid={`invoice-detail-rejection-reason-${inv.id}`}
                        className="p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-800 whitespace-pre-wrap"
                      >
                        <span className="font-semibold">Rejected:</span> {inv.rejectionReason}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setDetailModalOpen(true)}
                      className="w-full py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl shadow-sm transition-colors"
                    >
                      View Full Details
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* New / Edit dialogs */}
      <NewInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} />
      <NewInvoiceDialog
        open={editOpen}
        onOpenChange={(next) => {
          setEditOpen(next);
          if (!next) setEditInvoiceId(null);
        }}
        mode="edit"
        invoiceId={editInvoiceId ?? undefined}
        initialValues={editInitialValues}
      />

      {/* Delete confirm dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteConfirmId(null);
        }}
      >
        <DialogContent
          data-testid="invoice-delete-confirm"
          className="sm:max-w-sm"
        >
          <DialogHeader>
            <DialogTitle>Delete invoice?</DialogTitle>
            <DialogDescription>
              This will remove the invoice permanently. Attached proof documents
              are kept in the receipt store.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(null)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="invoice-delete-confirm-submit"
              onClick={() => {
                if (deleteConfirmId) void handleDelete(deleteConfirmId);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full detail modal */}
      <Dialog
        open={detailModalOpen && selectedInvoice !== null}
        onOpenChange={(next) => setDetailModalOpen(next)}
      >
        <DialogContent
          data-testid="invoices-hub-detail-modal"
          className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
        >
          {selectedInvoice && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedInvoice.counterparty}</DialogTitle>
                <DialogDescription>
                  Full invoice details and proof preview.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Direction
                  </div>
                  <div className="mt-1 text-slate-900 capitalize">
                    {selectedInvoice.direction}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </div>
                  <div className="mt-1">
                    <StatusBadge status={selectedInvoice.status} />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Amount
                  </div>
                  <div className="mt-1 text-slate-900 font-semibold">
                    {formatAmount(
                      selectedInvoice.amountCents,
                      selectedInvoice.currency,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Currency
                  </div>
                  <div className="mt-1 text-slate-900">
                    {selectedInvoice.currency}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Issued on
                  </div>
                  <div className="mt-1 text-slate-900">
                    {formatDate(selectedInvoice.issuedOn, "MMMM d, yyyy")}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Due on
                  </div>
                  <div className="mt-1 text-slate-900">
                    {selectedInvoice.dueOn
                      ? formatDate(selectedInvoice.dueOn, "MMMM d, yyyy")
                      : "—"}
                  </div>
                </div>
                {selectedInvoice.notes && (
                  <div className="col-span-2">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Notes
                    </div>
                    <div className="mt-1 text-slate-700 whitespace-pre-wrap">
                      {selectedInvoice.notes}
                    </div>
                  </div>
                )}
                <div className="col-span-2">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Proof
                  </div>
                  {linkedReceipt ? (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-2 flex items-center justify-center min-h-64">
                      <ReceiptPreview
                        testId="invoice-detail-modal-preview"
                        receipt={linkedReceipt}
                        alt={`Invoice proof for ${selectedInvoice.counterparty}`}
                        className="max-h-64 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-slate-500">
                      No proof attached.
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <button
                  type="button"
                  data-testid="invoices-hub-detail-edit"
                  onClick={() => {
                    setDetailModalOpen(false);
                    openEdit(selectedInvoice.id);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md"
                >
                  Edit
                </button>
                <button
                  type="button"
                  data-testid="invoices-hub-detail-delete"
                  onClick={() => {
                    setDetailModalOpen(false);
                    setDeleteConfirmId(selectedInvoice.id);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Delete
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ImportCsvDialog
        mode="invoice"
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={(count) => {
          setImportOpen(false);
          setImportSuccessCount(count);
        }}
      />
    </div>
  );
}
